import { createStyles, FormHelperText, makeStyles, MenuItem, Select, Theme } from '@material-ui/core';
import { FormRow } from '../form/FormRow';
import { Label } from '../form/Label';
import { FormField } from '../form/FormField';
import { Input } from '../form/Input';
import { insuranceCompanies } from '../../constants/insuranceCompanies';
import { useDispatch, useSelector } from 'react-redux';
import { selectPatient } from '../../store/registration/registration.selector';
import { selectUser } from '../../store/auth/auth.selector';
import { registrationActions } from '../../store/registration/registration.reducer';
import { ToggleButtons } from '../ToggleButtons';
import { ToggleButton } from '../ToggleButton';
import { Colors } from '../../constants/colors';
import { PersonalNumberFormField } from '../form/PersonalNumberFormField';
import { RequestType } from '../../model/enums/RequestType';
import { EmailFormField } from '../form/EmailFormField';
import { PhoneFormField } from '../form/PhoneFormField';

type PacientInformationProps = {
    emailValidonChangeEmailValid?: ((valid: boolean) => void) | undefined,
    onChangePhoneValid?: ((valid: boolean) => void) | undefined
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    accent: {
      color: Colors.primary
    },
    toggleButton: {
      [theme.breakpoints.down('xs')]: {
        display: 'block !important'
      }
    }
  })
);

export const PatientInformation = (props: PacientInformationProps) => {
    const classes = useStyles();
    const user = useSelector(selectUser);
    const patient = useSelector(selectPatient);
    const dispatch = useDispatch();

    return (
      <>
        <form onSubmit={(e) => e.preventDefault()}>
          <FormRow>
            <FormField>
              <Label>Titul před jménem:</Label>
              <Input onChange={e => dispatch(registrationActions.setPatient({ titleBefore: e.target.value }))}
                     value={patient.titleBefore}
                     name='titleBefore' />
            </FormField>
            <FormField>
              <Label required={true}>Jméno:</Label>
              <Input onChange={e => dispatch(registrationActions.setPatient({ firstName: e.target.value }))}
                     value={patient.firstName}
                     name='firstName' />
            </FormField>
          </FormRow>

          <FormRow>
            <FormField>
              <Label required={true}>Příjmení:</Label>
              <Input onChange={e => dispatch(registrationActions.setPatient({ lastName: e.target.value }))}
                     value={patient.lastName}
                     name='lastName' />
            </FormField>
            <FormField>
              <Label>Titul za jménem:</Label>
              <Input onChange={e => dispatch(registrationActions.setPatient({ titleAfter: e.target.value }))}
                     value={patient.titleAfter}
                     name='titleAfter' />
            </FormField>
          </FormRow>

          <FormField>
            <ToggleButtons flow='horizontal'>
              <ToggleButton active={patient.hasPersonalNumber}
                            className={classes.toggleButton}
                            onClick={() => dispatch(registrationActions.setPatient({ hasPersonalNumber: true }))}
                            type='button'>
                {(() => {
                  const accentClass = patient.hasPersonalNumber ? classes.accent : '';
                  if (user?.requestType === RequestType.DoctorRegistration) {
                    return <>Pacient <span className={accentClass}>má </span>české rodné číslo</>;
                  } else {
                    return <><span className={accentClass}>Mám </span>české rodné číslo</>;
                  }
                })()}
              </ToggleButton>
              <ToggleButton active={!patient.hasPersonalNumber}
                            className={classes.toggleButton}
                            onClick={() => dispatch(registrationActions.setPatient({ hasPersonalNumber: false }))}
                            type='button'>
                {(() => {
                  const accentClass = !patient.hasPersonalNumber ? classes.accent : '';
                  if (user?.requestType === RequestType.DoctorRegistration) {
                    return <>Pacient <span className={accentClass}>nemá </span>české rodné číslo</>;
                  } else {
                    return <><span className={accentClass}>Nemám </span>české rodné číslo</>;
                  }
                })()}
              </ToggleButton>
            </ToggleButtons>
            <FormHelperText>
              {(() => {
                if (user?.requestType === RequestType.DoctorRegistration) {
                  return 'Vyberte zda má pacient přidělené české rodné číslo';
                } else {
                  return 'Vyberte zda máte přidělené české rodné číslo';
                }
              })()}
            </FormHelperText>
          </FormField>

          {(() => {
            if (patient.hasPersonalNumber) {
              return <>
                <FormRow>
                  <FormField>
                    <Label required={true}>Zdravotní pojišťovna:</Label>
                    <Select value={patient.insuranceCompany ? patient.insuranceCompany.numericCode : ''}
                            onChange={e => dispatch(registrationActions.setPatient({
                              insuranceCompany: insuranceCompanies.find(company => company.numericCode === e.target.value)
                            }))}
                            input={<Input />}>
                      {insuranceCompanies.map((company, key) =>
                        <MenuItem key={key}
                                  value={company.numericCode}>
                          {company.numericCode !== -1 ? company.numericCode + ' - ' : null}{company.csFullName}
                        </MenuItem>
                      )}
                      <MenuItem value=''>- Nevybráno - </MenuItem>
                    </Select>
                    <FormHelperText>
                      {(() => {
                        if (user?.requestType === RequestType.DoctorRegistration) {
                          return 'Vyberte, pokud je pacient pojištěn u některé ze zdravotních pojišťoven ČR';
                        } else {
                          return 'Vyberte, pokud jste pojištěn u některé ze zdravotních pojišťoven ČR';
                        }
                      })()}
                    </FormHelperText>
                  </FormField>
                </FormRow>

                <FormRow>
                  <PersonalNumberFormField required={patient.hasPersonalNumber}
                                           requestType={user?.requestType}
                                           defaultValue={patient.personalNumber} />
                </FormRow>
              </>;
            } else {
              return <FormField>
                <Label required={!patient.hasPersonalNumber}>Číslo pojištěnce:</Label>
                <Input onChange={e => dispatch(registrationActions.setPatient({ insuranceNumber: e.target.value }))}
                       value={patient.insuranceNumber}
                       name='insuranceNumber' />
                <FormHelperText>
                  {(() => {
                    if (user?.requestType === RequestType.DoctorRegistration) {
                      return 'Zadejte číslo pojištěnce uvedené na kartičce zdravotní pojišťovny pacienta';
                    } else {
                      return 'Zadejte číslo pojištěnce uvedené na Vaší kartičce zdravotní pojišťovny';
                    }
                  })()}
                </FormHelperText>
              </FormField>;
            }
          })()}

          <FormRow>
            <EmailFormField required={false}
                            onChangeValid={props.emailValidonChangeEmailValid}
                            onChangeValue={(email: string) => dispatch(registrationActions.setPatient({ email: email }))} />
            <PhoneFormField required={true} disabled={user?.requestType === RequestType.PatientRegistration}
                            initialValue={patient.phone}
                            onChangeValid={props.onChangePhoneValid}
                            onChangeValue={(phone: string) => dispatch(registrationActions.setPatient({ phone: phone }))} />
          </FormRow>

          <FormField>
            <Label>Poznámka:</Label>
            <Input multiline={true}
                   rows={5}
                   onChange={e => dispatch(registrationActions.setPatient({ note: e.target.value }))}
                   value={patient.note}
                   name='note' />
          </FormField>
        </form>
      </>
    );
  }
;
